
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { del, entries, get, setMany, UseStore } from 'idb-keyval';
import { Observable, from, merge, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap, timeout } from 'rxjs/operators';
import { rehydrateAction, rehydrateErrorAction, rehydrateInitAction } from './ngrx-store-idb.actions';
import { NgrxStoreIdbStoreProvider } from './ngrx-store-idb-store.provider';
import { NgrxStoreIdbTenantResolver } from './ngrx-store-idb-tenant-resolver';
import { recordsToState, stateToRecords } from './ngrx-store-idb.flush';
import { NgrxStoreIdbOptions, OPTIONS, SAVED_STATE_KEY, SAVED_STATE_PREFIX } from './ngrx-store-idb.options';

@Injectable({
  providedIn: 'root',
})
export class RehydrateEffects implements OnInitEffects {
  private actions$ = inject(Actions);
  private options = inject<NgrxStoreIdbOptions>(OPTIONS);
  private storeProvider = inject(NgrxStoreIdbStoreProvider);
  private tenantResolver = inject(NgrxStoreIdbTenantResolver);

  // State read from IDB
  rehydratedState: unknown;

  /**
   * This effect is triggered after root store initialisation.
   * This is the very first thing done by NgRx and the best place
   * to load saved data.
   *
   * When the tenant (and therefore the store) is not known yet, an EMPTY
   * rehydrate is dispatched immediately: application startup must not wait
   * for the store, because on a fresh visit the tenant only resolves
   * through the auth flow, and the auth flow itself only starts once a
   * rehydrate outcome has been dispatched. The real rehydrate then runs as
   * soon as initTenantStore$ creates the store.
   */
  rehydrateOnInit$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(rehydrateInitAction),
      mergeMap(() => {
        const deferredRehydrate$ = this.storeProvider.store$.pipe(
          take(1),
          mergeMap(idbStore => this.loadSavedState(idbStore)),
        );
        if (this.storeProvider.getIfReady() == null) {
          return merge(
            of(rehydrateAction({ rehydratedState: null })),
            deferredRehydrate$,
          );
        }
        return deferredRehydrate$;
      }),
    ); },
  );

  /**
   * Once the application resolves the tenant, initialise the tenant store
   * if bootstrap couldn't (fresh visit), or reload if the bootstrap tenant
   * turned out to be the wrong one (e.g. another user logged in on this tab).
   */
  initTenantStore$ = createEffect(
    () =>
      { return this.tenantResolver.tenantId$().pipe(
        take(1),
        tap((tenantId) => {
          if (this.storeProvider.tenantIdInUse == null) {
            this.storeProvider.init(tenantId);
          } else if (this.storeProvider.tenantIdInUse !== tenantId) {
            this.tenantResolver.onTenantMismatch(tenantId);
          }
        }),
      ); },
    { dispatch: false },
  );

  private loadSavedState(idbStore: UseStore): Observable<Action> {
    if (this.options.debugInfo) {
      // This is only enabled if options.debugInfo is set to true
      // eslint-disable-next-line no-console
      console.debug('NgrxStoreIdb: Load state from IndexedDB');
    }
    return from(this.readState(idbStore)).pipe(
      // The read can queue indefinitely when a deleteDatabase is pending in
      // another tab; degrade to a rehydrate error instead of hanging startup
      // (the auth flow waits on a rehydrate outcome).
      timeout(this.options.concurrency?.refreshRate ?? 5000),
      tap(value => (this.rehydratedState = value)),
      tap((value) => {
        if (this.options.debugInfo) {
          // This is only enabled if options.debugInfo is set to true
          // eslint-disable-next-line no-console
          console.debug('NgrxStoreIdb: Loaded state from IndexedDB:', value);
        }
      }),
      map(value => rehydrateAction({ rehydratedState: value })),
      catchError((err) => {
        console.error('NgrxStoreIdb: Error reading state from IndexedDB', err);
        return of(rehydrateErrorAction());
      }),
    );
  }

  /**
   * Reads the persisted state: from the per-slice record layout when `keys`
   * are configured, falling back (once) to the legacy single record, which is
   * then migrated to per-slice records off the critical path.
   */
  private async readState(idbStore: UseStore): Promise<unknown> {
    if (!this.options.keys) {
      return get(SAVED_STATE_KEY, idbStore);
    }
    const allRecords = await entries(idbStore);
    const stateRecords = allRecords.filter(
      ([recordKey]) => typeof recordKey === 'string' && recordKey.startsWith(SAVED_STATE_PREFIX),
    );
    if (stateRecords.length > 0) {
      return recordsToState(stateRecords);
    }
    const legacyState = await get(SAVED_STATE_KEY, idbStore);
    if (legacyState != null) {
      this.migrateLegacyRecord(legacyState, idbStore);
    }
    return legacyState;
  }

  private migrateLegacyRecord(legacyState: unknown, idbStore: UseStore): void {
    const records = stateToRecords(legacyState, this.options.keys, this.options.perEntryKeys ?? []);
    setMany(records, idbStore)
      .then(() => del(SAVED_STATE_KEY, idbStore))
      .catch(err =>
        console.error('NgrxStoreIdb: Error migrating legacy state record', err),
      );
  }

  ngrxOnInitEffects(): Action {
    if (this.options.rehydrate) {
      return rehydrateInitAction();
    }
  }
}
